import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { BaseDatatableStore, IBaseDatatableState } from '../../../../state/base-datatable-store.service';
import { ELoadStatus } from '../../../../state/state.interface';
import {
  HttpUtilitiesService,
  IBaseCrudResponse,
  IBaseResponse,
  IGenericCrudRequestConstructionParameters,
  IGetManyResponse,
} from '../../../../utilities/http-utilities.service';
import { IFormAutofill } from './form-autofill.interface';

@Injectable()
export class FormAutofillStore extends BaseDatatableStore<IFormAutofill, IBaseDatatableState<IFormAutofill>> {
  readonly loadFormAutofill = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params: IGenericCrudRequestConstructionParameters) => {
        const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);

        this.patchState({ dataLoading: ELoadStatus.loading });

        return this.getFormAutofill(httpParams).pipe(
          tapResponse(
            (response: IGetManyResponse<IFormAutofill>) =>
              this.patchState({
                data: response.data,
                dataLoading: ELoadStatus.success,
                dataTotal: response.total,
              }),
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  override readonly deleteOne = this.effect((trigger$: Observable<number>) =>
    trigger$.pipe(
      switchMap((id: number) => {
        this.patchState({ singleCrudLoading: ELoadStatus.loading });

        return this.deleteFormAutofill(id).pipe(
          tapResponse(
            () => this.patchState({ singleCrudLoading: ELoadStatus.success }),
            (error) => console.error('Error deleting data', error),
          ),
        );
      }),
    ),
  );

  override readonly deleteBulk = this.effect((trigger$: Observable<number[]>) =>
    trigger$.pipe(
      switchMap((ids: number[]) => {
        this.patchState({ bulkCrudLoading: ELoadStatus.loading });

        return this.deleteFormAutofills(ids).pipe(
          tapResponse(
            (response): void => {
              this.patchState({
                bulkCrudLoading: ELoadStatus.success,
                bulkOperationFailedData: response.data,
              });
            },
            (error) => console.error('Error bulk deleting data', error),
          ),
        );
      }),
    ),
  );

  constructor(
    private readonly http: HttpClient,
    private readonly httpUtilities: HttpUtilitiesService,
  ) {
    super({
      bulkCrudLoading: ELoadStatus.initial,
      bulkOperationFailedData: [],
      data: [],
      dataLoading: ELoadStatus.initial,
      dataTotal: 0,
      singleCrudLoading: ELoadStatus.initial,
    });
  }

  private getFormAutofill(params: HttpParams): Observable<IGetManyResponse<IFormAutofill>> {
    return this.http.get<IGetManyResponse<IFormAutofill>>('form-autofills', { params });
  }

  private deleteFormAutofill(id: number): Observable<IBaseResponse<IFormAutofill>> {
    return this.http.delete<IBaseResponse<IFormAutofill>>(`form-autofills/${id}`);
  }

  private deleteFormAutofills(ids: number[]): Observable<IBaseResponse<IBaseCrudResponse[]>> {
    return this.http.delete<IBaseResponse<IBaseCrudResponse[]>>('form-autofills/bulk/delete', {
      body: { payload: ids },
    });
  }
}
